import { notifyAuthExpired } from '@/lib/authSession';

const API_URL = '/api';

// ============================================================================
// API Error
// ============================================================================

export class ApiError extends Error {
  status: number;
  statusText: string;
  details: unknown;

  constructor(response: Response, details: unknown) {
    const message =
      (typeof details === 'string' && details.length > 0 && details) ||
      (details && typeof details === 'object' && 'message' in details
        ? String((details as { message?: string }).message)
        : undefined) ||
      (details && typeof details === 'object' && 'error' in details
        ? String((details as { error?: string }).error)
        : undefined) ||
      response.statusText ||
      `Request failed with status ${response.status}`;
    super(message);
    this.name = 'ApiError';
    this.status = response.status;
    this.statusText = response.statusText;
    this.details = details;
  }
}

// ============================================================================
// Auth Helpers
// ============================================================================

function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

function getAuthHeaders(additionalHeaders: Record<string, string> = {}): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    ...additionalHeaders,
  };

  if (token) {
    if (!headers.Authorization) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Provide a header fallback for environments where Authorization may be stripped
    if (!headers['x-access-token']) {
      headers['x-access-token'] = token;
    }
  }

  return headers;
}

/**
 * The request never reached the server (offline, DNS failure, server down).
 * Distinct from ApiError so the offline queue can retry these and only these.
 */
export class NetworkError extends Error {
  constructor(message = 'Unable to connect to server. Please check that the server is running.') {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

// ============================================================================
// API Fetch Options
// ============================================================================

export interface ApiFetchOptions {
  path: string;
  method?: string;
  accountId?: string;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  formData?: FormData;
}

// ============================================================================
// Low-level Fetch (returns Response)
// ============================================================================

export async function apiFetch(options: ApiFetchOptions): Promise<Response> {
  const {
    path,
    method = 'GET',
    accountId,
    query,
    body,
    signal,
    headers = {},
    formData,
  } = options;

  const url = new URL(`${API_URL}${path}`, window.location.origin);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) {
        continue;
      }
      url.searchParams.set(key, String(value));
    }
  }

  if (accountId) {
    url.searchParams.set('account_id', accountId);
  }

  const init: RequestInit = {
    method,
    signal,
  };

  if (formData) {
    init.body = formData;
    init.headers = getAuthHeaders(headers);
  } else if (body !== undefined && body !== null) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
    init.headers = getAuthHeaders({ 'Content-Type': 'application/json', ...headers });
  } else {
    init.headers = getAuthHeaders(headers);
  }

  try {
    const response = await fetch(url.toString(), init);

    if (response.status === 401 || response.status === 403) {
      notifyAuthExpired();
    }

    return response;
  } catch (error) {
    // Network errors (server unreachable, DNS failure, etc.) — typed so
    // callers (offline queue) can tell connectivity loss apart from real
    // server errors.
    if (error instanceof Error && error.name !== 'AbortError') {
      throw new NetworkError();
    }
    throw error;
  }
}

// ============================================================================
// High-level Request (parses response, throws on error)
// ============================================================================

export async function apiRequest<T = unknown>(options: ApiFetchOptions): Promise<T> {
  const response = await apiFetch(options);

  const contentType = response.headers.get('content-type') || '';
  let data: unknown = null;

  try {
    if (contentType.includes('application/json')) {
      data = await response.json();
    } else if (contentType.startsWith('text/')) {
      data = await response.text();
    } else if (response.status === 204 || response.status === 205) {
      data = null;
    } else if (response.ok) {
      data = await response.arrayBuffer();
    } else {
      data = await response.text();
    }
  } catch (error) {
    // Ignore abort errors during response parsing
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    if (response.ok) {
      console.warn('Failed to parse API response', error);
    }
  }

  if (!response.ok) {
    // If we got HTML content (like a 404 page), replace it with a friendly message
    if (typeof data === 'string' && (data.trim().startsWith('<!DOCTYPE') || data.trim().startsWith('<html'))) {
      data = 'Unable to connect to server. Please check that the server is running.';
    }
    throw new ApiError(response, data);
  }

  return data as T;
}

// ============================================================================
// Event Stream URL Helper
// ============================================================================

// EventSource can't send an Authorization header, so the stream is opened with a
// short-lived single-use ticket fetched with the normal Bearer header. The session
// JWT itself must never go in a URL — URLs are written to proxy/access logs.
export async function getEventsUrl(accountId?: string, lastEventId?: string | null): Promise<string> {
  const { ticket } = await apiRequest<{ ticket: string }>({ path: '/events/ticket', method: 'POST' });
  const url = new URL(`${API_URL}/events`, window.location.origin);
  url.searchParams.set('ticket', ticket);
  if (accountId) {
    url.searchParams.set('account_id', accountId);
  }
  // EventSource can't set request headers, so on our manual reconnect we pass the
  // last-seen event id as a query param; the server replays everything newer so
  // events emitted during the disconnect aren't lost (audit §2.10 #6).
  if (lastEventId) {
    url.searchParams.set('lastEventId', lastEventId);
  }
  return url.toString();
}
